<script setup>
import { computed } from "vue";
import {
  BadgeBGColorB2B,
  BadgeBGColorB2C,
  BadgePaddingB2B,
  BadgePaddingB2C,
  BadgeBorderColorB2B,
  BadgeBorderColorB2C,
  BadgeTextColorB2B,
  BadgeTextColorB2C,
  BadgeType
} from "../../../interfaces/UI";
const props = defineProps({
  type: {
    type: String,
    default: BadgeType.INFO
  },
  isB2B: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: "default"
  },
  indicator: {
    type: Boolean,
    default: false
  },
  color: {
    type: String,
    default: void 0
  },
  withBorder: {
    type: Boolean,
    default: false
  },
  customIconClass: {
    type: String,
    default: void 0
  },
  customTextSize: {
    type: String,
    default: void 0
  },
  disabled: {
    type: Boolean,
    default: false
  }
});
const sizeClass = computed(() => {
  switch (props.size) {
    case "extra-small":
      return props.isB2B ? BadgePaddingB2B.XS : BadgePaddingB2C.XS;
    case "small":
      return props.isB2B ? BadgePaddingB2B.SMALL : BadgePaddingB2C.SMALL;
    case "large":
      return props.isB2B ? BadgePaddingB2B.LARGE : BadgePaddingB2C.LARGE;
    case "extra-large":
      return props.isB2B ? BadgePaddingB2B.XL : BadgePaddingB2C.XL;
    default:
      return props.isB2B ? BadgePaddingB2B.MEDIUM : BadgePaddingB2C.MEDIUM;
  }
});
const textClass = computed(() => {
  let baseClass = "";
  if (props.customTextSize) {
    baseClass = props.customTextSize;
  } else {
    switch (props.size) {
      case "extra-small":
      case "small":
        baseClass = "text-2xs";
        break;
      case "large":
        baseClass = "text-sm";
        break;
      case "extra-large":
        baseClass = "text-base";
        break;
      default:
        baseClass = "text-xs";
    }
  }
  return props.disabled ? `${baseClass} text-gray-disabled` : baseClass;
});
const bgColor = computed(() => {
  if (props.disabled) {
    return props.isB2B ? BadgeBGColorB2B.NEUTRAL : BadgeBGColorB2C.NEUTRAL;
  }
  if (props.color) {
    return `${props.color}-200`;
  }
  const badgeBGColor = props.isB2B ? BadgeBGColorB2B : BadgeBGColorB2C;
  switch (props.type) {
    case BadgeType.SUCCESS:
      return badgeBGColor.SUCCESS;
    case BadgeType.NEUTRAL:
      return badgeBGColor.NEUTRAL;
    case BadgeType.WARNING:
      return badgeBGColor.WARNING;
    case BadgeType.ERROR:
      return badgeBGColor.ERROR;
    default:
      return badgeBGColor.INFO;
  }
});
const indicatorColor = computed(() => {
  if (props.color) {
    return `${props.color}-500`;
  }
  switch (props.type) {
    case BadgeType.SUCCESS:
      return "green-500";
    case BadgeType.NEUTRAL:
      return "zinc-500";
    case BadgeType.WARNING:
      return "orange-500";
    case BadgeType.ERROR:
      return "red-500";
    default:
      return "primary-400";
  }
});
const textColor = computed(() => {
  if (props.disabled) {
    return "gray-disabled";
  }
  if (props.color) {
    return `${props.color}-900`;
  }
  const badgeTextColor = props.isB2B ? BadgeTextColorB2B : BadgeTextColorB2C;
  switch (props.type) {
    case BadgeType.SUCCESS:
      return badgeTextColor.SUCCESS;
    case BadgeType.NEUTRAL:
      return badgeTextColor.NEUTRAL;
    case BadgeType.WARNING:
      return badgeTextColor.WARNING;
    case BadgeType.ERROR:
      return badgeTextColor.ERROR;
    default:
      return badgeTextColor.INFO;
  }
});
const borderColor = computed(() => {
  if (props.color) {
    return `${props.color}-300`;
  }
  const badgeBorderColor = props.isB2B ? BadgeBorderColorB2B : BadgeBorderColorB2C;
  switch (props.type) {
    case BadgeType.SUCCESS:
      return badgeBorderColor.SUCCESS;
    case BadgeType.NEUTRAL:
      return badgeBorderColor.NEUTRAL;
    case BadgeType.WARNING:
      return badgeBorderColor.WARNING;
    case BadgeType.ERROR:
      return badgeBorderColor.ERROR;
    default:
      return badgeBorderColor.INFO;
  }
});
const iconClass = computed(
  () => !props.disabled && props.customIconClass || "size-3 text-gray-disabled"
);
</script>

<template>
    <div
        class="badge rounded-full flex flex-row gap-1 justify-center items-center h-fit"
        :class="[
  withBorder && 'border',
  `bg-${bgColor} border-${borderColor} text-${textColor}`,
  sizeClass
]"
    >
        <IconDot
            v-if="indicator"
            class="size-5"
            is-solid
            :class="[`text-${indicatorColor}`]"
        />
        <div v-if="$slots.icon" :class="iconClass">
            <slot name="icon" />
        </div>
        <span :class="textClass" class="font-medium">
            <slot />
        </span>
    </div>
</template>
